import PopularArea from '@/components/PopularArea';
import RecentArea from '@/components/RecentArea';
import PopperInput from '@/containers/Desktop/PopperInput';
import { LocationSearchType } from '@/containers/SearchFlightTab/SearchDialog/index.d';
import SearchFlightResultItem from '@/containers/SearchFlightTab/SearchFlightResultItem';
import type { IFlightResultItem } from '@/containers/SearchFlightTab/SearchFlightResultItem/index.d';
import { useLazyGetAreaByNameQuery } from '@/slices/area/apis/areaApi';
import { style } from '@/styles/globals';
import { clearAnchorPopperInput } from '@/utils/eventEmitter';
import { useAppSelector, useAppDispatch } from '@/utils/hook';
import { pr } from '@/utils/pxToRem';
import { transformAreaData } from '@/utils/transformAreaData';
import { LOCATION_TYPE, LOCATION_TYPE_PRIORITY } from '@/utils/constants';
import { Box, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/system';
import { debounce, uniqueId } from 'lodash';
import { NoResultSearchIcon } from 'public/icons';
import React, {
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { addArea } from '@/slices/area/slices/areaSlice';
import { addHomePoints } from '@/slices/area/slices/searchFlightFormSlice';
import { ILangObj } from '@/slices/goyoloSeo/sliceType';

interface SelectAreaProps {
  type: LocationSearchType;
  lang?: ILangObj;
  point?: any,
  isFirstTime?: boolean;
  switchUpdateSecondTime?: any;
  vehicleType?: 'bus' | 'flight' | 'train';

}

const SelectArea: React.FC<SelectAreaProps> = ({
  type, lang, point, isFirstTime, switchUpdateSecondTime, vehicleType,
}) => {
  const dispatch = useAppDispatch();

  const [isInputEmpty, setIsInputEmpty] = useState(true);
  const [getAreaByName, { data, isFetching }] = useLazyGetAreaByNameQuery();

  const isInputEmptyRef = useRef(true);
  const debounceGetArea = useRef(debounce(getAreaByName, 100));
  const dataMapping = useRef<IFlightResultItem[]>([]);

  // Check if this is train tab based on vehicleType
  const isTrainTab = vehicleType === 'train';

  // Function to get Vietnamese title by type
  const getTitleKeyByType = useCallback((locationType: number) => {
    if (locationType === LOCATION_TYPE.AIRPORT_STATION) return isTrainTab ? 'Ga tàu' : 'Sân bay';
    if (locationType === LOCATION_TYPE.CITY) return 'Tỉnh/Thành phố';
    if (locationType === LOCATION_TYPE.DISTRICT) return 'Quận/Huyện';
    if (locationType === LOCATION_TYPE.WARD) return 'Phường/Xã';
    return 'Khu vực khác';
  }, [isTrainTab]);

  // Group and sort data by type with priority
  const groupedData = useMemo(() => {
    if (!dataMapping.current || dataMapping.current.length === 0) return [];

    // Group items by type
    const grouped = dataMapping.current.reduce((acc, item) => {
      const { type: itemType } = item;
      if (!acc[itemType]) {
        acc[itemType] = [];
      }
      acc[itemType].push(item);
      return acc;
    }, {} as Record<number, IFlightResultItem[]>);

    // Sort by priority and return as array of { type, title, items }
    return LOCATION_TYPE_PRIORITY
      .filter((priorityType) => grouped[priorityType] && grouped[priorityType].length > 0)
      .map((priorityType) => ({
        type: priorityType,
        title: getTitleKeyByType(priorityType),
        items: grouped[priorityType],
      }));
  }, [dataMapping.current, getTitleKeyByType]);

  const { startPointHome, toPointHome } = useAppSelector(
    (state) => state.searchFlightForm,
  );
  const areaPoint = type === LocationSearchType.FROM ? startPointHome : toPointHome;
  if (dataMapping.current) {
    dataMapping.current = data && (!isInputEmptyRef.current || areaPoint)
      ? transformAreaData(data)
      : [];
  }

  const [hoverIndex, setHoverIndex] = React.useState<any>(0);

  React.useEffect(() => {
    if (data) setHoverIndex(0);
  }, [data]);

  const refEl = React.useRef<any>();
  React.useEffect(() => {
    const handler = (e: any) => {
      const value = e?.detail || '';
      if (!value) return;
      isInputEmptyRef.current = false;
      setIsInputEmpty(false);
      try {
        debounceGetArea.current(value, true);
      } catch (err) {
        // no-op
      }
    };
    window.addEventListener('search.quickFill', handler as EventListener);
    return () => {
      window.removeEventListener('search.quickFill', handler as EventListener);
    };
  }, [debounceGetArea, setIsInputEmpty]);
  return (
    <PopperInput
      isFirstTime={isFirstTime}
      point={point}
      lang={lang}
      title={type === LocationSearchType.FROM ? (lang?.SearchBar?.flightsTab?.from || 'Nơi đi') : (lang?.SearchBar?.flightsTab?.to || 'Nơi đến')}
      vehicleType={vehicleType}
      setIsInputEmpty={setIsInputEmpty}
      type={type}
      debounceGetArea={debounceGetArea}
      isInputEmptyRef={isInputEmptyRef}
      onClickOutside={() => {
        if (!isInputEmptyRef.current && !isFetching && dataMapping.current) {
          dispatch(addArea({
            area: dataMapping.current?.[0],
          }));
          dispatch(addHomePoints({
            data: dataMapping.current?.[0],
            type,
          }));
        }
      }}
    >
      <ContentContainer
        ref={refEl}
        isInputEmpty={isInputEmpty}
      >
        {isInputEmpty && (
          <>
            <RecentArea
              vehicleType={vehicleType}
              lang={lang}
              isDesktop
              type={type}
              setClose={clearAnchorPopperInput}
            />
            <PopularArea
              vehicleType={vehicleType}
              lang={lang}
              isDesktop
              type={type}
              setClose={clearAnchorPopperInput}
            />
          </>
        )}
        {!isInputEmptyRef.current
          && !isFetching
          && groupedData.map((group, groupIndex) => (
            <React.Fragment key={group.type}>
              <GroupTitle>
                <GroupTitleText variant="body2">
                  {group.title}
                </GroupTitleText>
              </GroupTitle>
              {group.items.map((item, itemIndex) => {
                const globalIndex = groupedData
                  .slice(0, groupIndex)
                  .reduce((acc, g) => acc + g.items.length, 0) + itemIndex;
                return (
                  <SearchFlightResultItem
                    type={type}
                    isInputEmptyRef={isInputEmptyRef}
                    setClose={clearAnchorPopperInput}
                    isSearchResult
                    setIsInputEmpty={setIsInputEmpty}
                    key={uniqueId()}
                    data={item}
                    index={globalIndex}
                    hoverIndex={hoverIndex}
                    onMouseEnter={setHoverIndex}
                    lang={lang}
                  />
                );
              })}
            </React.Fragment>
          ))}
        {isFetching && (
          <StyleLoading>
            <Box
              sx={{
                display: 'flex',
                pb: pr`16px`,
              }}
            >
              <CircularProgress color="inherit" />
            </Box>
            <Typography variant="body2">
              {lang?.SearchBar?.flightsTab?.waiting || 'Vui lòng đợi. Tìm kiếm của bạn đang được xử lý'}
            </Typography>
          </StyleLoading>
        )}
        {data?.length === 0 && !isInputEmptyRef.current && !isFetching && (
          <StyleLoading>
            <Box
              sx={{
                display: 'flex',
                pb: pr`16px`,
              }}
            >
              <NoResultSearchIcon />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: style.color.black2,
                fontWeight: '700',
              }}
            >
              {lang?.SearchBar?.flightsTab?.notFound || 'Rất tiếc Goyolo không tìm thấy địa điểm này'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: style.color.black2,
                fontSize: '12px',
                lineHeight: '16px',
                mt: '8px',
              }}
            >
              {lang?.SearchBar?.flightsTab?.notFoundHelp || 'Bạn vui lòng nhập tỉnh/thành phố, quận/huyện, sân bay để tìm.'}
            </Typography>
          </StyleLoading>
        )}
      </ContentContainer>
    </PopperInput>
  );
};

const StyleLoading = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 50%;
  align-items: center;
  padding: 0 18px
  `;

const GroupTitle = styled('div')`
  background-color: #f8f9fa;
  border-bottom: 1px solid #e8edf1;
  position: sticky;
  top: 0px;
  z-index: 1;
`;

const GroupTitleText = styled(Typography)`
  color: ${style.color.black2};
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  padding: ${pr`8px`} ${pr`16px`};
`;

export const ContentContainer = styled(Box)<{ isInputEmpty?: boolean }>`
  padding-bottom: 16px;
  padding-top: ${({ isInputEmpty }) => (isInputEmpty ? '16px' : '0px')};
  border: 1px solid #e8edf1;
  box-sizing: border-box;
  box-shadow: 0px 2px 10px rgb(7 65 57 / 15%);
  border-radius: 6px;
  background: white;
  max-height: 472px;
  overflow-y: scroll;
  overflow-x: hidden;
`;

export default SelectArea;
